import { proof } from "./dpop";
import { dpopKey } from "./keystore";
import * as session from "./session";
import requirements from "./requestCapabilities.generated";

export class SignInRequired extends Error {}
export class SessionEnded extends Error {}

/** How a signed request leaves the browser: fetch, or XMLHttpRequest for uploads with progress (xhr.ts). */
export type Transport = (url: string, init: RequestInit) => Promise<Response>;
const viaFetch: Transport = (url, init) => fetch(url, init);

function permissionResponse(
  response: Response,
  method: string,
  path: string,
  scopes: string[] | null,
): Response {
  const segments = path.split("?")[0]!.split("/").filter(Boolean);
  const required = requirements.find(
    (item) =>
      item.method === method.toUpperCase() &&
      item.segments.length === segments.length &&
      item.segments.every(
        (part, index) => part === null || part === segments[index],
      ),
  )?.capability;
  if (
    response.status !== 403 ||
    (!/\berror="insufficient_scope"/.test(
      response.headers.get("WWW-Authenticate") ?? "",
    ) &&
      !(required && scopes && !scopes.includes(required)))
  )
    return response;
  if (typeof window !== "undefined")
    window.dispatchEvent(new Event("emdur.permissions-widened"));
  return new Response(
    JSON.stringify({
      type: "https://errors.emdur.com/insufficient-scope",
      title: "Sign in again to refresh permissions",
      status: 403,
    }),
    { status: 403, headers: response.headers },
  );
}

let cached: { api_url: string; client_id: string } | null = null;

export async function config() {
  cached ??= (await (await fetch("/config.json")).json()) as {
    api_url: string;
    client_id: string;
  };
  return cached;
}

// PAR and token go through the console proxy (same origin), which adds the client assertion; the proof's
// htu is the Foundation's own endpoint, because that is what the Foundation checks.
const oauthNonces = new Map<string, string>();
export async function oauth(
  endpoint: "par" | "token" | "revoke",
  form: URLSearchParams,
  generation = session.version(),
): Promise<Response> {
  const { api_url } = await config();
  if (generation !== session.version())
    throw new SessionEnded("This sign-in ended.");
  const keys = await dpopKey();
  const send = async (nonce?: string) => {
    if (generation !== session.version())
      throw new SessionEnded("This sign-in ended.");
    const response = await fetch(`/oauth/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        DPoP: await proof(keys, "POST", `${api_url}/oauth/${endpoint}`, {
          nonce,
        }),
      },
      body: form.toString(),
      signal: AbortSignal.timeout(15000),
    });
    const latest = response.headers.get("DPoP-Nonce");
    if (latest && generation === session.version())
      oauthNonces.set(endpoint, latest);
    return response;
  };
  const first = await send(oauthNonces.get(endpoint));
  if (first.status !== 400) return first;
  const nonce = first.headers.get("DPoP-Nonce");
  const error = (
    (await first
      .clone()
      .json()
      .catch(() => ({}))) as { error?: string }
  ).error;
  return error === "use_dpop_nonce" && nonce ? send(nonce) : first;
}

let refreshing: Promise<boolean> | null = null;
export function clearCredentialState() {
  oauthNonces.clear();
  refreshing = null;
}
function refresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  const pending = refreshOnce().finally(() => {
    if (refreshing === pending) refreshing = null;
  });
  refreshing = pending;
  return refreshing;
}
async function refreshOnce(): Promise<boolean> {
  const generation = session.version();
  const token = await session.refreshToken();
  if (!token) return false;
  const r = await oauth(
    "token",
    new URLSearchParams({ grant_type: "refresh_token", refresh_token: token }),
    generation,
  );
  if (!r.ok) return false;
  await session.setTokens(await r.json(), generation);
  return generation === session.version();
}

/** A request to the API on the person's DPoP-bound token. The caller's headers and raw body are kept; Authorization
 * and a fresh DPoP proof are added, and an Idempotency-Key for anything but GET unless the caller gave one. An expired
 * token is refreshed once and the request sent again; no session at all throws SignInRequired. */
export async function request(
  method: string,
  path: string,
  init: { headers?: HeadersInit; body?: BodyInit | null } = {},
  transport: Transport = viaFetch,
): Promise<Response> {
  const generation = session.version();
  const { api_url } = await config();
  let token = session.accessToken();
  if (!token && (await refresh())) token = session.accessToken();
  if (generation !== session.version())
    throw new SessionEnded("This sign-in ended.");
  if (!token) throw new SignInRequired();
  const keys = await dpopKey();
  const scopes = session.accessScopes();
  const send = async (token: string) => {
    if (generation !== session.version())
      throw new SessionEnded("This sign-in ended.");
    const headers = new Headers(init.headers);
    headers.set("Authorization", `DPoP ${token}`);
    headers.set(
      "DPoP",
      await proof(keys, method, `${api_url}${path}`, { accessToken: token }),
    );
    if (method !== "GET" && !headers.has("Idempotency-Key"))
      headers.set("Idempotency-Key", crypto.randomUUID());
    if (generation !== session.version())
      throw new SessionEnded("This sign-in ended.");
    return transport(`${api_url}${path}`, {
      method,
      headers,
      body: init.body ?? undefined,
    });
  };
  const first = await send(token);
  if (generation !== session.version())
    throw new SessionEnded("This sign-in ended.");
  if (first.status !== 401)
    return permissionResponse(first, method, path, scopes);
  const renewed = await refresh();
  if (generation !== session.version())
    throw new SessionEnded("This sign-in ended.");
  if (!renewed) throw new SignInRequired();
  const retried = await send(session.accessToken()!);
  if (generation !== session.version())
    throw new SessionEnded("This sign-in ended.");
  if (retried.status === 401) throw new SignInRequired();
  return permissionResponse(retried, method, path, session.accessScopes());
}

/** A JSON request (the existing pages): the body as JSON, an optional Idempotency-Key of the caller's. */
export async function call(
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  return request(method, path, {
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

// Unauthenticated JSON calls (invitation inspection and acceptance, passkey assertions).
export async function publicCall(
  method: string,
  path: string,
  body?: unknown,
): Promise<Response> {
  const { api_url } = await config();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (method !== "GET") headers["Idempotency-Key"] = crypto.randomUUID();
  return fetch(`${api_url}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
